/**
 * DiagAuditTrail: Diagnostics drawer -- chain discovery, filter waterfall and chain resolver plan, moved verbatim out of src/pages/ScreenView.tsx (Screener Phase 3, 2026-10-28). Display only.
 */
import React from "react";
import { AnalyticsMetric, badgeStyle, mutedText, secondaryText, ui } from "@components/screener/screenerUi";
import { MICRO_LABEL } from "@components/cockpit";
import { toneFromTrustStatus, topCountText } from "@lib/screenerViewHelpers";
import { numberFmt, pctFmt, titleizeCode } from "@lib/screenerTruth";
import { type ChainAudit, type FilterAudit, type FilterAuditExpiryCoverageRow, type FilterAuditWaterfallRow, type ScreenerResponse } from "@lib/screenerTypes";

export type DiagAuditTrailProps = {
  auditWaterfall: FilterAuditWaterfallRow[];
  chainAudit: ChainAudit | null;
  chainExpiryPlan: Record<string, any>[];
  data: ScreenerResponse;
  farthestExpiryCoverage: FilterAuditExpiryCoverageRow[];
  filterAudit: FilterAudit | null;
};

export default function DiagAuditTrail({
  auditWaterfall,
  chainAudit,
  chainExpiryPlan,
  data,
  farthestExpiryCoverage,
  filterAudit,
}: DiagAuditTrailProps) {
  return (
    <div className="rounded-2xl border p-3" style={{ borderColor: ui.cardBorder }}>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="max-w-4xl">
          <div className={MICRO_LABEL} style={mutedText}>
            Screener audit trail
          </div>
          <div className="mt-1 font-semibold">
            Chain discovery and filter waterfall
          </div>
          <div className="mt-1 text-sm leading-6" style={secondaryText}>
            Why the universe expanded, shrank, or lost far-dated expiries. It separates IBKR chain discovery from Helion&rsquo;s DTE, moneyness, OI, quote, and Greek filters.
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <span style={badgeStyle(chainAudit?.explicitAllExpiries ? "success" : "warning")}>
            {chainAudit?.explicitAllExpiries ? "All expiries" : "Expiry cap active"}
          </span>
          <span style={badgeStyle(toneFromTrustStatus(filterAudit?.status))}>
            Filter: {String(filterAudit?.status || "—").toUpperCase()}
          </span>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-6">
        <AnalyticsMetric
          label="Expiry scope"
          value={chainAudit?.explicitAllExpiries ? "All DTE-qualified" : "Capped"}
          subtext={`months_limit ${chainAudit?.monthsLimit ?? data.meta.inputs?.months_limit ?? "—"}; fetch band ${pctFmt(chainAudit?.fetchWindowBandPct ?? null, 2)}`}
        />
        <AnalyticsMetric
          label="Expiries raw / used"
          value={`${numberFmt(chainAudit?.expiriesTotal ?? null, 0)} / ${numberFmt(chainAudit?.expiriesUsed ?? null, 0)}`}
          subtext={`After DTE: ${numberFmt(chainAudit?.expiriesAfterDte ?? null, 0)}; farthest used ${(chainAudit?.expiriesUsedList || []).slice(-1)[0] || "—"}`}
        />
        <AnalyticsMetric
          label="Specs / resolved"
          value={`${numberFmt(chainAudit?.expectedSpecs ?? null, 0)} / ${numberFmt(chainAudit?.resolvedContracts ?? null, 0)}`}
          subtext={`Unique conids ${numberFmt(chainAudit?.uniqueConids ?? null, 0)}; path ${chainAudit?.resolverPath || "—"}`}
        />
        <AnalyticsMetric
          label="Qualified contracts"
          value={numberFmt(filterAudit?.qualifiedCount ?? null, 0)}
          subtext={`Rejected ${numberFmt(filterAudit?.rejectedTotal ?? null, 0)}${filterAudit?.auditRowsTruncated ? "; samples truncated" : ""}`}
        />
        <AnalyticsMetric
          label="Liquidity policy"
          value={`Min OI ${numberFmt(filterAudit?.minOpenInterest ?? data.meta.inputs?.min_open_interest ?? null, 0)}`}
          subtext={`50–99 OI starter tier; missing OI ${filterAudit?.allowMissingOiForRank ? "ranked for TWS verification" : "rejected"}`}
        />
        <AnalyticsMetric
          label="Farthest qualified"
          value={filterAudit?.maxQualifiedExpiry || "—"}
          subtext={`Farthest resolved ${filterAudit?.maxRawExpiry || "—"}`}
        />
      </div>

      {filterAudit?.longDatedProbe?.explanation?.length ? (
        <div className="mt-4 rounded-2xl border p-3 text-sm" style={{ borderColor: ui.cardBorder, backgroundColor: "rgba(148, 163, 184, 0.06)" }}>
          <div className="mb-1 font-semibold">Long-dated probe</div>
          <div className="space-y-1" style={secondaryText}>
            {filterAudit.longDatedProbe.explanation.map((line, idx) => (
              <div key={`long-probe-${idx}`}>{line}</div>
            ))}
          </div>
        </div>
      ) : null}

      <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-[0.9fr_1.1fr]">
        <div className="overflow-x-auto rounded-2xl border" style={{ borderColor: ui.cardBorder }}>
          <table className="min-w-full text-sm">
            <thead style={{ backgroundColor: ui.rowHeader }}>
              <tr>
                <th className="px-3 py-2 text-left font-semibold" style={{ color: ui.textSecondary }}>Stage</th>
                <th className="px-3 py-2 text-right font-semibold" style={{ color: ui.textSecondary }}>Count</th>
                <th className="px-3 py-2 text-right font-semibold" style={{ color: ui.textSecondary }}>Raw %</th>
                <th className="px-3 py-2 text-right font-semibold" style={{ color: ui.textSecondary }}>Dropped</th>
              </tr>
            </thead>
            <tbody>
              {auditWaterfall.length ? auditWaterfall.map((row, idx) => (
                <tr key={`${row.stage || "stage"}-${idx}`} style={{ borderTop: `1px solid ${ui.cardBorder}`, backgroundColor: idx % 2 ? "transparent" : ui.rowAlt }}>
                  <td className="px-3 py-2">
                    <div className="font-semibold">{row.label || titleizeCode(row.stage)}</div>
                    {row.dropReason ? <div className="text-xs" style={mutedText}>{titleizeCode(row.dropReason)}</div> : null}
                  </td>
                  <td className="px-3 py-2 text-right">{numberFmt(row.count ?? null, 0)}</td>
                  <td className="px-3 py-2 text-right">{pctFmt(row.shareOfRaw ?? null, 1)}</td>
                  <td className="px-3 py-2 text-right">{row.droppedFromPrior == null ? "—" : numberFmt(row.droppedFromPrior, 0)}</td>
                </tr>
              )) : (
                <tr><td colSpan={4} className="px-3 py-3" style={mutedText}>No filter waterfall returned.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="overflow-x-auto rounded-2xl border" style={{ borderColor: ui.cardBorder }}>
          <table className="min-w-full text-sm">
            <thead style={{ backgroundColor: ui.rowHeader }}>
              <tr>
                <th className="px-3 py-2 text-left font-semibold" style={{ color: ui.textSecondary }}>Farthest expiries</th>
                <th className="px-3 py-2 text-right font-semibold" style={{ color: ui.textSecondary }}>Raw</th>
                <th className="px-3 py-2 text-right font-semibold" style={{ color: ui.textSecondary }}>OI</th>
                <th className="px-3 py-2 text-right font-semibold" style={{ color: ui.textSecondary }}>Mid</th>
                <th className="px-3 py-2 text-right font-semibold" style={{ color: ui.textSecondary }}>Vega</th>
                <th className="px-3 py-2 text-right font-semibold" style={{ color: ui.textSecondary }}>Qualified</th>
                <th className="px-3 py-2 text-left font-semibold" style={{ color: ui.textSecondary }}>Top blockers</th>
              </tr>
            </thead>
            <tbody>
              {farthestExpiryCoverage.length ? farthestExpiryCoverage.map((row, idx) => (
                <tr key={`${row.expiry || "expiry"}-${idx}`} style={{ borderTop: `1px solid ${ui.cardBorder}`, backgroundColor: idx % 2 ? "transparent" : ui.rowAlt }}>
                  <td className="px-3 py-2 font-semibold">{row.expiry || "—"}</td>
                  <td className="px-3 py-2 text-right">{numberFmt(row.raw ?? null, 0)}</td>
                  <td className="px-3 py-2 text-right">{numberFmt(row.oi ?? null, 0)}</td>
                  <td className="px-3 py-2 text-right">{numberFmt(row.mid ?? null, 0)}</td>
                  <td className="px-3 py-2 text-right">{numberFmt(row.vega ?? null, 0)}</td>
                  <td className="px-3 py-2 text-right">{numberFmt(row.qualified ?? null, 0)}</td>
                  <td className="px-3 py-2">{topCountText(row.dropCounts || null, 3)}</td>
                </tr>
              )) : (
                <tr><td colSpan={7} className="px-3 py-3" style={mutedText}>No expiry coverage returned.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {chainExpiryPlan.length ? (
        <div className="mt-4">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide" style={mutedText}>Chain resolver plan — farthest expiries</div>
          <div className="overflow-x-auto rounded-2xl border" style={{ borderColor: ui.cardBorder }}>
            <table className="min-w-full text-sm">
              <thead style={{ backgroundColor: ui.rowHeader }}>
                <tr>
                  <th className="px-3 py-2 text-left font-semibold" style={{ color: ui.textSecondary }}>Expiry</th>
                  <th className="px-3 py-2 text-right font-semibold" style={{ color: ui.textSecondary }}>Window strikes</th>
                  <th className="px-3 py-2 text-right font-semibold" style={{ color: ui.textSecondary }}>Resolved</th>
                  <th className="px-3 py-2 text-left font-semibold" style={{ color: ui.textSecondary }}>Window</th>
                </tr>
              </thead>
              <tbody>
                {chainExpiryPlan.map((row, idx) => (
                  <tr key={`${row.expiry || "plan"}-${idx}`} style={{ borderTop: `1px solid ${ui.cardBorder}`, backgroundColor: idx % 2 ? "transparent" : ui.rowAlt }}>
                    <td className="px-3 py-2 font-semibold">{String(row.expiry || "—")}</td>
                    <td className="px-3 py-2 text-right">{numberFmt(Number(row.fetchWindowStrikes ?? row.windowStrikes ?? row.strikes ?? row.strikeCount ?? 0), 0)}</td>
                    <td className="px-3 py-2 text-right">{numberFmt(Number(row.sourceResolvedContracts ?? row.resolvedContracts ?? row.expectedSpecs ?? row.expected_specs ?? 0), 0)}</td>
                    <td className="px-3 py-2">{row.window ? String(row.window) : `${numberFmt(Number(row.fetchStrikeMin ?? row.sourceStrikeMin ?? row.lowerStrike ?? row.lower ?? 0), 0)}–${numberFmt(Number(row.fetchStrikeMax ?? row.sourceStrikeMax ?? row.upperStrike ?? row.upper ?? 0), 0)}`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}
